import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ProtectedRoute from "./ProtectRoute";
import PublicRoute from "./PublicRoute";
import PublicDocumentRoute from "./PublicDocumentRoute";
import RoleSelectorModal from "../components/auth/RoleSelectorModal";
import LoadingAnimation from "../components/loading/LoadingAnimation";
import {
  COMPLETE_HELP_ROUTES,
  OPERATIONS_ROUTES,
  PERSON_ROUTES,
  VEHICLES_ROUTES,
  RATES_ROUTES,
  MANIFEST_ROUTES,
  PREOP_ROUTES,
} from "./routes.config";

// Layouts cargados estáticamente (necesarios para el shell visual inmediato)
import PublicLayout from "../layout/PublicLayout";

// Providers scoped por ruta (no globales)
import {
  ChatProviders,
  WhatsAppProviders,
  MultiTrackingProviders,
  MapsProviders,
  HelpProviders,
  VehicleFilterProviders,
  RecaptchaProviders,
  TransportProviders,
  DashboardMonitorProviders,
  OperationsProviders,
  VehiclesProviders,
  PreoperacionalProviders,
} from "../providers/RouteProviders";

// --- Lazy loading de páginas ---
// Públicas
const HomePage = React.lazy(() => import("../page/public/HomePage"));
const Introduction = React.lazy(() => import("../page/public/Introduction"));
const AttendanceQR = React.lazy(() => import("../page/public/AttendanceQR"));
const LoginSection = React.lazy(() => import("../page/auth/section/Login"));
const PasswordRecoverySection = React.lazy(() => import("../page/auth/section/PasswordRecoverySection"));

// Dashboards
const DashboardThirdParty = React.lazy(() => import("../page/thirdParty/DashboardThirdParty"));
const DashboardVehicle = React.lazy(() => import("../page/vehicle/view_vehicle/DashboardVehicle"));
const DashboardMonitor = React.lazy(() => import("../page/admin/DashboardMonitor"));
const DashboardGuest = React.lazy(() => import("../page/guest/DashboardGuest"));
const DashboardCUV = React.lazy(() => import("../page/cuv/DashboardCUV"));
const DashboardLUV = React.lazy(() => import("../page/luv/DashboardLUV"));
const DashboardHumanResource = React.lazy(() => import("../page/human_resources/DashboardHumanResource"));
const DashboardVehicleDocuments = React.lazy(() => import("../page/vehicle/DashboardVehicleDocuments"));
const DashboardMaps = React.lazy(() => import("../page/maps/DashboardMaps"));
const DashboardCarrier = React.lazy(() => import("../page/carriers/DashboardCarrier"));
const DashboardMantenimiento = React.lazy(() => import("../page/mantenimiento/DashboardMantenimiento"));

// Admin
const RegisterSection = React.lazy(() => import("../page/auth/section/RegisterSection"));
const Trends = React.lazy(() => import("../page/admin/Trends"));
const Hotspots = React.lazy(() => import("../page/admin/Hotspots"));
const RepeatOffenders = React.lazy(() => import("../page/admin/RepeatOffenders"));
const DocumentUploader = React.lazy(() => import("../page/admin/DocumentUploader"));
const SicetacAdminPanel = React.lazy(() => import("../page/admin/sicetac/SicetacAdminPanel"));
const Persons = React.lazy(() => import("../page/admin/Persons"));
const Operations = React.lazy(() => import("../page/admin/Operations"));

// Vehículos y Tracking
const Vehicles = React.lazy(() => import("../page/vehicle/Vehicles"));
const TrackingView = React.lazy(() => import("../page/vehicle/TrackingView"));
const MultiVehicleTracking = React.lazy(() => import("../page/vehicle/MultiVehicleTracking.jsx"));
const VehicleInspectionView = React.lazy(() => import("../page/vehicle/VehicleInspectionView"));
const LocationReportView = React.lazy(() => import("../page/vehicle/LocationReportView"));

// Transporte
const TransportCalculatorContainer = React.lazy(() => import("../page/transport/TransportCalculatorContainer"));
const TransportCalculatorRedirect = React.lazy(() => import("../page/transport/TransportCalculatorRedirect"));

// Otros
const MessageView = React.lazy(() => import("../page/MessageView"));
const WsspConnection = React.lazy(() => import("../page/whastapp/WsspConnection.jsx"));
const Rates = React.lazy(() => import("../page/rates/Rates"));
const Help = React.lazy(() => import("../page/help/Help"));
const ManifestLayout = React.lazy(() => import("../page/manifests/ManifestLayout"));
const PendingChangesInbox = React.lazy(() => import("../page/pending-changes/PendingChangesInbox.jsx"));
const PendingChangesDashboard = React.lazy(() => import("../page/pending-changes/PendingChangesDashboard.jsx"));
const OperationsTreeView = React.lazy(() => import("../page/admin/operations_tree/OperationsTreeView"));
const GlobalSearch = React.lazy(() => import("../page/search/GlobalSearch"));

// Preoperacional
const PreoperacionalConfig = React.lazy(() => import("../page/preoperacional/PreoperacionalConfig.jsx"));
const PreoperacionalGerenciamiento = React.lazy(() => import("../page/preoperacional/PreoperacionalGerenciamiento.jsx"));
const PreoperacionalMantenimiento = React.lazy(() => import("../page/preoperacional/PreoperacionalMantenimiento.jsx"));

// HSE / SARLAFT
const HseDashboard = React.lazy(() => import("../page/hse/HseDashboard"));
const HseConfiguracion = React.lazy(() => import("../page/hse/HseConfiguracion"));
const HsePrevisualizar = React.lazy(() => import("../page/hse/HsePrevisualizar"));
const SarlaftFormPage = React.lazy(() => import("../page/hse/SarlaftFormPage"));

// Wrapper de Suspense para rutas lazy
const SuspenseRoute = ({ children }) => (
  <Suspense fallback={<LoadingAnimation />}>
    {children}
  </Suspense>
);

SuspenseRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const AppRoutes = () => {
  const { user, selectedRole } = useAuth();

  const getDashboardComponent = (role) => {
    switch (role) {
      case 1:
        return <DashboardHumanResource />;
      case 2:
        return <VehicleFilterProviders><DashboardVehicle /></VehicleFilterProviders>;
      case 3:
        return <DashboardThirdParty />;
      case 4:
        return <DashboardMonitorProviders><DashboardMonitor /></DashboardMonitorProviders>;
      case 8:
        return <DashboardMonitorProviders><DashboardMonitor /></DashboardMonitorProviders>;
      case 9:
        return <DashboardMonitorProviders><DashboardMonitor /></DashboardMonitorProviders>;
      case 10:
        return <DashboardMonitorProviders><DashboardMonitor /></DashboardMonitorProviders>;
      case 11:
        return <DashboardGuest />; // TDCLL Vehículos - Invitado
      case 12:
        return <DashboardGuest />; // TDCLL Usuarios - Invitado
      case 13:
        return <DashboardCUV />; // Completo Usuarios y Vehículos
      case 14:
        return <DashboardLUV />; // Limitado Usuarios y Vehículos
      case 15:
        return <DashboardMantenimiento />; // Mantenimiento (MTTO) — Fase de pruebas
      default:
        return <Navigate to="/" />;
    }
  };

  return (
    <>
      {/* Modal de selección de rol */}
      <RoleSelectorModal />
      
      <Suspense fallback={<LoadingAnimation />}>
        <Routes>
          {/* Rutas Públicas */}
          <Route
            path="/xd"
            element={
              <PublicLayout>
                <SuspenseRoute><Introduction /></SuspenseRoute>
              </PublicLayout>
            }
          />

          {/* Rutas para cada sección */}
          <Route
            path="/"
            element={
              <MapsProviders>
                <PublicLayout>
                  <SuspenseRoute><HomePage /></SuspenseRoute>
                </PublicLayout>
              </MapsProviders>
            }
          />
          <Route
            path="/acerca-de-nosotros"
            element={
              <MapsProviders>
                <PublicLayout>
                  <SuspenseRoute><HomePage /></SuspenseRoute>
                </PublicLayout>
              </MapsProviders>
            }
          />
          <Route
            path="/aliados"
            element={
              <MapsProviders>
                <PublicLayout>
                  <SuspenseRoute><HomePage /></SuspenseRoute>
                </PublicLayout>
              </MapsProviders>
            }
          />
          <Route
            path="/contacto"
            element={
              <MapsProviders>
                <PublicLayout>
                  <SuspenseRoute><HomePage /></SuspenseRoute>
                </PublicLayout>
              </MapsProviders>
            }
          />
          <Route
            path="/assistance-QR"
            element={
              <RecaptchaProviders>
                <PublicLayout>
                  <SuspenseRoute><AttendanceQR /></SuspenseRoute>
                </PublicLayout>
              </RecaptchaProviders>
            }
          />

          {/* Ruta de login */}
          <Route
            path="/login"
            element={
              <RecaptchaProviders>
                <PublicRoute requiresNoAuth={true}>
                  <SuspenseRoute><LoginSection /></SuspenseRoute>
                </PublicRoute>
              </RecaptchaProviders>
            }
          />
          {/* Rutas con autenticacion - Modo EDICIÓN */}
          <Route
            path="/transport-calculator"
            element={
              <ProtectedRoute>
                <TransportProviders>
                  <SuspenseRoute><TransportCalculatorRedirect /></SuspenseRoute>
                </TransportProviders>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transport-calculator/:documentId"
            element={
              <ProtectedRoute>
                <TransportProviders>
                  <SuspenseRoute><TransportCalculatorContainer isEditMode={true} /></SuspenseRoute>
                </TransportProviders>
              </ProtectedRoute>
            }
          />

          {/* Panel de Administración SICETAC - Requiere autenticación */}
          <Route
            path="/sicetac-admin"
            element={
              <ProtectedRoute>
                <SuspenseRoute><SicetacAdminPanel /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* Ruta pública - Modo SOLO LECTURA */}
          <Route
            path="/document/:documentId"
            element={
              <TransportProviders>
                <PublicDocumentRoute>
                  <SuspenseRoute><TransportCalculatorContainer isEditMode={false} /></SuspenseRoute>
                </PublicDocumentRoute>
              </TransportProviders>
            }
          />
          <Route
            path="/recoverey"
            element={
              <PublicRoute requiresNoAuth={true}>
                <SuspenseRoute><PasswordRecoverySection /></SuspenseRoute>
              </PublicRoute>
            }
          />
          <Route
            path="/wssp-connection"
            element={
              <ProtectedRoute>
                <WhatsAppProviders>
                  <SuspenseRoute><WsspConnection /></SuspenseRoute>
                </WhatsAppProviders>
              </ProtectedRoute>
            }
          />
          {/* Administrador */}
          <Route
            path="/register"
            element={
              <ProtectedRoute>
                <SuspenseRoute><RegisterSection /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicle-tracking"
            element={
              <ProtectedRoute>
                <MapsProviders>
                  <SuspenseRoute><TrackingView /></SuspenseRoute>
                </MapsProviders>
              </ProtectedRoute>
            }
          />
          <Route
            path="/multi-tracking"
            element={
              <ProtectedRoute>
                <MultiTrackingProviders>
                  <MapsProviders>
                    <SuspenseRoute><MultiVehicleTracking /></SuspenseRoute>
                  </MapsProviders>
                </MultiTrackingProviders>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pending-changes"
            element={
              <ProtectedRoute>
                <SuspenseRoute><PendingChangesInbox /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pending-changes-dashboard"
            element={
              <ProtectedRoute>
                <SuspenseRoute><PendingChangesDashboard /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/validate-vehicle-inspection"
            element={
              <ProtectedRoute>
                <SuspenseRoute><VehicleInspectionView /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts-persons"
            element={
              <ProtectedRoute>
                <SuspenseRoute><DashboardHumanResource /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts-vehicles"
            element={
              <ProtectedRoute>
                <VehicleFilterProviders>
                  <SuspenseRoute><DashboardVehicleDocuments /></SuspenseRoute>
                </VehicleFilterProviders>
              </ProtectedRoute>
            }
          />
          <Route
            path="/geofences-travel-and-more"
            element={
              <ProtectedRoute>
                <MapsProviders>
                  <SuspenseRoute><DashboardMaps /></SuspenseRoute>
                </MapsProviders>
              </ProtectedRoute>
            }
          />

          <Route
            path="/location-reports"
            element={
              <ProtectedRoute>
                <SuspenseRoute><LocationReportView /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          {Object.entries(PERSON_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <SuspenseRoute><Persons routeConfig={route} /></SuspenseRoute>
                </ProtectedRoute>
              }
            />
          ))}
          {Object.entries(OPERATIONS_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <OperationsProviders>
                    <SuspenseRoute><Operations routeConfig={route} /></SuspenseRoute>
                  </OperationsProviders>
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="/trends"
            element={
              <ProtectedRoute>
                <SuspenseRoute><Trends /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hotspots"
            element={
              <ProtectedRoute>
                <SuspenseRoute><Hotspots /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/repeat-offenders"
            element={
              <ProtectedRoute>
                <SuspenseRoute><RepeatOffenders /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          {Object.entries(VEHICLES_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <VehiclesProviders>
                    <SuspenseRoute><Vehicles routeConfig={route} /></SuspenseRoute>
                  </VehiclesProviders>
                </ProtectedRoute>
              }
            />
          ))}
          {Object.entries(RATES_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <SuspenseRoute><Rates routeConfig={route} /></SuspenseRoute>
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="/carriers"
            element={
              <ProtectedRoute>
                <SuspenseRoute><DashboardCarrier /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/message"
            element={
              <ProtectedRoute>
                <ChatProviders>
                  <SuspenseRoute><MessageView /></SuspenseRoute>
                </ChatProviders>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <SuspenseRoute>
                  {user && selectedRole ? getDashboardComponent(selectedRole.id) : <Navigate to="/" />}
                </SuspenseRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <div>perfil</div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reportes"
            element={
              <ProtectedRoute>
                <div>reportes</div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/satellite-uploader"
            element={
              <ProtectedRoute>
                <SuspenseRoute><DocumentUploader /></SuspenseRoute>
              </ProtectedRoute>
            }
          />
          {Object.entries(COMPLETE_HELP_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <HelpProviders>
                    <SuspenseRoute><Help routeConfig={route} /></SuspenseRoute>
                  </HelpProviders>
                </ProtectedRoute>
              }
            />
          ))}
          {Object.entries(MANIFEST_ROUTES).map(([key, route]) => (
            <Route
              key={key}
              path={route.path}
              element={
                <ProtectedRoute>
                  <SuspenseRoute><ManifestLayout routeConfig={route} /></SuspenseRoute>
                </ProtectedRoute>
              }
            />
          ))}

          {/* Árbol de operaciones — solo admin, no visible en leftnav */}
          <Route
            path="/operations-tree"
            element={
              <ProtectedRoute>
                <SuspenseRoute><OperationsTreeView /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* Búsqueda global — vehículos y personas */}
          <Route
            path="/global-search"
            element={
              <ProtectedRoute>
                <SuspenseRoute><GlobalSearch /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* Preoperacional — Configuración RRHH (Gerenciamiento de viajes) */}
          <Route
            path={PREOP_ROUTES.configHSEQ.path}
            element={
              <ProtectedRoute>
                <SuspenseRoute><PreoperacionalConfig tipo="hseq" /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* Preoperacional — Configuración MTTO (Mantenimiento) */}
          <Route
            path={PREOP_ROUTES.configMTTO.path}
            element={
              <ProtectedRoute>
                <SuspenseRoute><PreoperacionalConfig tipo="mantenimiento" /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* Preoperacional — Gerenciamiento de viajes (RRHH) */}
          <Route
            path={PREOP_ROUTES.gerenciamiento.path}
            element={
              <ProtectedRoute>
                <PreoperacionalProviders>
                  <SuspenseRoute><PreoperacionalGerenciamiento /></SuspenseRoute>
                </PreoperacionalProviders>
              </ProtectedRoute>
            }
          />

          {/* Preoperacional — Mantenimiento (MTTO) */}
          <Route
            path={PREOP_ROUTES.preoperacional.path}
            element={
              <ProtectedRoute>
                <PreoperacionalProviders>
                  <SuspenseRoute><PreoperacionalMantenimiento /></SuspenseRoute>
                </PreoperacionalProviders>
              </ProtectedRoute>
            }
          />

          {/* HSE / SARLAFT — Dashboard de formularios recibidos */}
          <Route
            path="/hse/dashboard"
            element={
              <ProtectedRoute>
                <SuspenseRoute><HseDashboard /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* HSE / SARLAFT — Configuracion (incluye generacion de token publico) */}
          <Route
            path="/hse/configuracion"
            element={
              <ProtectedRoute>
                <SuspenseRoute><HseConfiguracion /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* HSE / SARLAFT — Previsualizar (modo preview autenticado) */}
          <Route
            path="/hse/previsualizar"
            element={
              <ProtectedRoute>
                <SuspenseRoute><HsePrevisualizar /></SuspenseRoute>
              </ProtectedRoute>
            }
          />

          {/* SARLAFT — Formulario publico (con token ?tk=...). Sin auth, sin sidebar */}
          <Route
            path="/sarlaft-public"
            element={
              <RecaptchaProviders>
                <PublicLayout>
                  <SuspenseRoute><SarlaftFormPage mode="public" /></SuspenseRoute>
                </PublicLayout>
              </RecaptchaProviders>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoutes;
